import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ACTIVE_STATUS,
  listPrincipalCandidates,
  transferTenantPrincipal,
  type Tenant,
  type TenantPrincipalCandidate,
  type TenantPrincipalTransfer,
} from "./tenantsApi";
import { ApiError, SessionExpiredError } from "@web/lib/api";
import { Modal } from "@web/ui";

// Handing over tenant-pääkäyttäjyys from the platform-admin tenant console (US-E9.4, FR-Q.36) — the
// PÄÄKÄYTTÄJYYS section of the tenant's own page (`pages/TenantPage.tsx`).
//
// It is its OWN named action and deliberately NOT a role editor: the seat moves from one named person to
// another in a single audited operation, which is exactly what US-E9.4 forbids happening "roolin
// sivutuotteena". So it is a section the operator opens by name rather than a Rooli dropdown.
//
// WHY THE CANDIDATE READ IS BEHIND A CLICK, ON A PAGE THAT READS OTHER THINGS ON LOAD.
// `GET …/principal-candidates` writes a `TENANT_ACCESSED` audit row (`{"viewed":"tenant_principals"}`,
// FR-Q.35) — reading a named tenant's PEOPLE across the isolation boundary IS the audited event. Opening
// this page is the operator's explicit choice of the TENANT, which is why the provisioning status is read
// on load; it is not a choice to look at who works there. So this read happens when the operator opens
// the handover and never before, and the closed state SAYS so rather than leaving it a silent property.
// `e2e/platform-tenant.realstack.spec.ts` stands guard over it: opening the page must issue no request to
// this route.
//
// TWO STEPS. The section is the form: who holds it today, who receives it. The second step is an
// `alertdialog`, and it NAMES BOTH PEOPLE — the operator is about to take an authority away from someone,
// and the confirmation is where that is stated rather than implied. It stays a modal now that the first
// step is inline: a decision point that scrolled past with the page would not be one.
//
// Every refusal gets its own message, because they mean different things to the operator: 409 says the
// state moved under them, 403 says they aimed it at themselves, 502 says the identity provider never
// answered. In all of those the seat is still where it was. The 500 is the one case where it may not be,
// and its message says so instead of inviting a retry.

/** The phase this handover is in. The candidate list, once read, is held beside it. */
type Phase =
  /** Nothing has been read: the section offers the action and names the audit row opening it writes. */
  | { kind: "closed" }
  | { kind: "loading" }
  | { kind: "loadError" }
  | { kind: "pick" }
  /** The alertdialog is up, naming `to` as the incoming holder. */
  | { kind: "confirm"; to: TenantPrincipalCandidate }
  /** The transfer is in flight — the dialog may not be taken away under it. */
  | { kind: "working"; to: TenantPrincipalCandidate };

/** A candidate as a human reads them: the name, and the address when there is one to disambiguate by. */
function label(person: TenantPrincipalCandidate): string {
  return person.email ? `${person.name} (${person.email})` : person.name;
}

/**
 * Can the seat be handed TO this person? The server is the authority — it also checks the IdP identity and
 * refuses a self-appointment, neither of which is visible here — so this only keeps the picker from
 * offering people it can already tell would be refused: a crypto-shredded account, or one that is not
 * active. Somebody who ALREADY holds pääkäyttäjyys is left out too: this operation moves the seat to
 * someone who does not have it, and handing it to a second holder would be a revoke wearing the name of a
 * transfer.
 */
function canReceive(person: TenantPrincipalCandidate): boolean {
  return !person.isPrincipal && !person.isShredded && person.status === ACTIVE_STATUS;
}

export function TenantPrincipalSection({
  tenant,
  headingId,
  onDone,
  onHolder,
}: {
  tenant: Tenant;
  /** The id of this section's own <h2> — the page owns the heading, this owns everything under it. */
  headingId: string;
  /** A completed handover: both parties in their new state, for the page's success banner. */
  onDone: (transferred: TenantPrincipalTransfer) => void;
  /**
   * Who holds pääkäyttäjyys, once this section has read it — for the page's section nav. Null means "no
   * holder"; the nav is NOT told anything before the read, because the read is the audited act and the
   * nav is not a reason to perform it.
   */
  onHolder: (holder: string | null) => void;
}) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>({ kind: "closed" });
  const [candidates, setCandidates] = useState<TenantPrincipalCandidate[]>([]);
  /** Which holder hands it over. Pre-selected when the tenant has exactly one (the normal case). */
  const [fromUserId, setFromUserId] = useState<string | null>(null);
  const [toUserId, setToUserId] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const openRef = useRef<HTMLButtonElement>(null);
  const successorRef = useRef<HTMLSelectElement>(null);
  /** Aborts the candidate read if the operator closes the section before it lands. */
  const readRef = useRef<AbortController | null>(null);
  /**
   * The section has just OPENED and the keyboard still has to follow it. A flag read by an effect rather
   * than a focus call in the response handler: the select the focus goes to is rendered by the same state
   * update, so the only point at which it certainly exists is after React has committed. The flag is what
   * keeps this from also firing when a cancelled confirmation returns to `pick` — the Modal hands focus
   * back to the button that opened it, and a second claim here would take it away again.
   */
  const focusOnOpen = useRef(false);

  useEffect(() => () => readRef.current?.abort(), []);

  useEffect(() => {
    if (phase.kind !== "pick" || !focusOnOpen.current) return;
    focusOnOpen.current = false;
    successorRef.current?.focus();
  }, [phase]);

  // THE audited read. It runs here, from a click, and from nowhere else in this app.
  const onOpen = useCallback(() => {
    setError(null);
    setPhase({ kind: "loading" });
    const controller = new AbortController();
    readRef.current = controller;
    listPrincipalCandidates(tenant.id, controller.signal)
      .then((people) => {
        setCandidates(people);
        const found = people.filter((p) => p.isPrincipal);
        setFromUserId(found.length === 1 ? found[0].userId : null);
        focusOnOpen.current = true;
        setPhase({ kind: "pick" });
        onHolder(found.length > 0 ? found.map((p) => p.name).join(", ") : null);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || err instanceof SessionExpiredError) return;
        setPhase({ kind: "loadError" });
      });
  }, [tenant.id, onHolder]);

  /** Collapse back to the closed state and hand the keyboard back to the control that opened it. */
  const onCollapse = useCallback(() => {
    readRef.current?.abort();
    setError(null);
    setToUserId("");
    setPhase({ kind: "closed" });
    queueMicrotask(() => openRef.current?.focus());
  }, []);

  const holders = candidates.filter((p) => p.isPrincipal);
  const outgoing = candidates.find((p) => p.userId === fromUserId) ?? null;
  const successors = candidates.filter(canReceive);

  // Step 1 → step 2: nothing is sent yet, the confirmation is.
  const onRequestConfirm = useCallback(() => {
    setError(null);
    const to = candidates.find((p) => p.userId === toUserId);
    if (!outgoing || !to) {
      setError(t("tenants.principal.chooseRequired"));
      return;
    }
    setPhase({ kind: "confirm", to });
  }, [candidates, toUserId, outgoing, t]);

  // Step 2 answered yes. One call; every outcome but success leaves the seat where it was (except the
  // 500, whose message says so).
  const onConfirm = useCallback(
    (to: TenantPrincipalCandidate) => {
      if (!outgoing) return;
      setPhase({ kind: "working", to });
      transferTenantPrincipal(tenant.id, { fromUserId: outgoing.userId, toUserId: to.userId })
        .then((transferred) => {
          // The section closes on success: the list it holds is now wrong in the one way that matters
          // (both parties changed), and re-reading it would file a second audit row nobody asked for.
          setCandidates([]);
          setToUserId("");
          setPhase({ kind: "closed" });
          onHolder(transferred.to.name);
          onDone(transferred);
        })
        .catch((err: unknown) => {
          if (err instanceof SessionExpiredError) return;
          setError(t(transferErrorKey(err)));
          // Back to the form: the refusals are things the operator can react to (re-read the holders,
          // pick somebody else), and a dead alertdialog is not where that happens.
          setPhase({ kind: "pick" });
        });
    },
    [outgoing, tenant.id, onDone, onHolder, t],
  );

  const busy = phase.kind === "working";
  const confirmTitleId = "principal-confirm-title";
  const confirmBodyId = "principal-confirm-body";
  const pending = phase.kind === "confirm" || phase.kind === "working" ? phase.to : null;

  return (
    <>
      <h2 id={headingId}>{t("tenants.principal.sectionTitle")}</h2>
      <p className="principal__intro">{t("tenants.principal.intro")}</p>

      {/* CLOSED — the default, and the state this section spends almost all its life in. The dashed box
          is not decoration: it is where the operator is told that the people are not on screen yet and
          that asking for them is itself recorded. A section that fetched them quietly would be the same
          surface with the audit row hidden. */}
      {phase.kind === "closed" && (
        <div className="principal__closed">
          <p className="principal__closed-note" role="note" data-testid="principal-audit-note">
            {t("tenants.principal.auditNote")}
          </p>
          <button
            type="button"
            className="btn"
            ref={openRef}
            onClick={onOpen}
            data-testid="tenant-principal-open"
          >
            {t("tenants.principal.rowAction")}
          </button>
        </div>
      )}

      {phase.kind === "loading" && (
        <p className="empty-state" role="status" aria-live="polite">
          {t("tenants.principal.loading")}
        </p>
      )}

      {phase.kind === "loadError" && (
        <p className="banner banner--error" role="alert" data-testid="principal-load-error">
          {t("tenants.principal.loadError")}
        </p>
      )}

      {phase.kind !== "closed" && phase.kind !== "loading" && (
        <>
          {phase.kind !== "loadError" && (
            <div className="form principal__form">
              {/* The current holder, read from the server — never assumed, and never editable here. */}
              {holders.length === 0 ? (
                <p className="banner banner--error" role="alert" data-testid="principal-no-holder">
                  {t("tenants.principal.noHolder")}
                </p>
              ) : holders.length === 1 ? (
                <>
                  <span className="principal__label">{t("tenants.principal.currentHolder")}</span>
                  <p className="principal__holder" data-testid="principal-current">
                    {label(holders[0])}
                  </p>
                </>
              ) : (
                <>
                  {/* A tenant with two holders must not have the wrong one stripped: the operation names
                      whose seat moves, so when there is more than one, the operator says which. */}
                  <label htmlFor="principal-from">{t("tenants.principal.currentHolderChoose")}</label>
                  <select
                    id="principal-from"
                    value={fromUserId ?? ""}
                    onChange={(e) => {
                      setFromUserId(e.target.value || null);
                      setToUserId("");
                    }}
                    data-testid="principal-from"
                  >
                    <option value="">{t("tenants.principal.choosePlaceholder")}</option>
                    {holders.map((holder) => (
                      <option key={holder.userId} value={holder.userId}>
                        {label(holder)}
                      </option>
                    ))}
                  </select>
                </>
              )}

              {holders.length > 0 && (
                <>
                  <label htmlFor="principal-to">{t("tenants.principal.newHolder")}</label>
                  <select
                    id="principal-to"
                    ref={successorRef}
                    value={toUserId}
                    onChange={(e) => setToUserId(e.target.value)}
                    disabled={successors.length === 0}
                    data-testid="principal-to"
                  >
                    <option value="">{t("tenants.principal.choosePlaceholder")}</option>
                    {successors.map((person) => (
                      <option key={person.userId} value={person.userId}>
                        {label(person)}
                      </option>
                    ))}
                  </select>
                  {successors.length === 0 && (
                    <p className="principal__note" data-testid="principal-no-candidates">
                      {t("tenants.principal.noCandidates")}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {error && (
            <p className="field-error" role="alert" data-testid="principal-error">
              {error}
            </p>
          )}

          <div className="tenant-section__actions">
            <button
              type="button"
              className="btn"
              onClick={onCollapse}
              disabled={busy}
              data-testid="principal-cancel"
            >
              {holders.length > 0 ? t("tenants.principal.cancel") : t("tenants.principal.close")}
            </button>
            {holders.length > 0 && (
              <button
                type="button"
                className="btn btn--primary"
                onClick={onRequestConfirm}
                disabled={busy || !outgoing || toUserId === ""}
                data-testid="principal-submit"
              >
                {t("tenants.principal.submit")}
              </button>
            )}
          </div>
        </>
      )}

      {/* The decision point. Still a modal now that the picker is inline: it must be answered, and a
          confirmation that could be scrolled away from is not a decision point. The backdrop stays
          presentational — an accidental click is not an answer. */}
      {pending && (
        <Modal
          role="alertdialog"
          className="principal"
          labelledBy={confirmTitleId}
          describedBy={confirmBodyId}
          testId="principal-confirm"
          onClose={() => setPhase({ kind: "pick" })}
          closeDisabled={busy}
        >
          <h2 id={confirmTitleId}>{t("tenants.principal.confirmTitle")}</h2>
          <p id={confirmBodyId} data-testid="principal-confirm-body">
            {t("tenants.principal.confirmBody", {
              from: outgoing ? label(outgoing) : "",
              to: label(pending),
            })}
          </p>
          <p className="principal__tenant">
            {t("tenants.principal.confirmTenant", { tenant: tenant.slug })}
          </p>
          <div className="modal__actions">
            <button
              type="button"
              className="btn"
              onClick={() => setPhase({ kind: "pick" })}
              disabled={busy}
              data-testid="principal-confirm-cancel"
            >
              {t("tenants.principal.cancel")}
            </button>
            <button
              type="button"
              className="btn btn--danger"
              onClick={() => onConfirm(pending)}
              disabled={busy}
              data-testid="principal-confirm-submit"
            >
              {busy ? t("tenants.principal.working") : t("tenants.principal.confirm")}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

/**
 * The message a refused handover gets. One per outcome, because they ask different things of the
 * operator — and the 500 is deliberately NOT "try again": that is the case where the role may already
 * have moved without its tenant-side audit row, and a blind retry would be refused as a 409 at best.
 */
function transferErrorKey(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.status) {
      case 403:
        return "tenants.principal.error403";
      case 404:
        return "tenants.principal.error404";
      case 409:
        return "tenants.principal.error409";
      case 422:
        return "tenants.principal.error422";
      case 500:
        return "tenants.principal.error500";
      case 502:
        return "tenants.principal.error502";
      default:
        return "tenants.principal.errorNetwork";
    }
  }
  // Never reached the server (offline, aborted transport): nothing was transferred.
  return "tenants.principal.errorNetwork";
}
